import React, { FC, useEffect } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  TextField,
} from "@mui/material";
import { Control, Controller, useFormContext } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  Question,
  ScenarioData,
  fetchUpdatedData,
} from "../slices/specialArticleSlice";
import { AddSpecialArticleInput } from "./SpecialAddModal";

interface Props {
  data: ScenarioData;
  control: Control<AddSpecialArticleInput>;
  errors?: Record<string, any>;
}

const BuildSpecialForm: FC<Props> = ({ data, control, errors = {} }) => {

  const { setValue } = useFormContext<AddSpecialArticleInput>();
  const dispatch = useAppDispatch();
  const dataQuestions = useAppSelector(
    (state) => state.specialArticleSlice.data
  );




  useEffect(() => {
    data.Questions?.forEach((question) => {
      if (question.QuestionCode && question.val) {
        setValue(question.QuestionCode, question.val);
      }
    });
  }, [data.Questions, setValue , dispatch]);

  return (
    <>
      {data.Questions?.map((item) => {
        if (!item.isActif) return null;
        const activeDataValues = item.DataValues?.filter(dv => dv.isActif);
  
        
        return (
          <Grid item xs={12} key={item.LineNumber}>
            {item.TypeReponse !== "CHECKBOX" &&
              item.TypeReponse !== "DROPDOWN" && (
                <InputLabel htmlFor={item.QuestionCode}>
                  {item.QuestionContent}
                </InputLabel>
              )}
            {item.TypeReponse === "DROPDOWN" && (
              <DropdownQuestion
                item={{ ...item, DataValues: activeDataValues }}
                control={control}
                errors={errors}
                dataQuestions={dataQuestions}
                dispatch={dispatch}
                data={data}
              />
            )}
            {item.TypeReponse === "MINMAX" && (
              <MinMaxQuestion
                item={item}
                control={control}
                errors={errors}
                dataQuestions={dataQuestions}
                dispatch={dispatch}
                data={data}
              />
            )}
            {item.TypeReponse === "CHECKBOX" && (
              <CheckboxQuestion
                item={item}
                control={control}
                dataQuestions={dataQuestions}
                dispatch={dispatch}
                data={data}
              />
            )}
            {item.TypeReponse === null && (
              <TextQuestion
                item={item}
                control={control}
                errors={errors}
                dataQuestions={dataQuestions}
                dispatch={dispatch}
                data={data}
              />
            )}
          </Grid>
        );
      })}
    </>
  );
};

interface QuestionProps {
  item: Question;
  control: Control<AddSpecialArticleInput>;
  errors?: Record<string, any>;
  dataQuestions?: any;
  dispatch?: any;
  data?: ScenarioData;
}

const DropdownQuestion: FC<QuestionProps> = ({
  item,
  control,
  errors = {},
  dataQuestions,
  dispatch,
  data,
}) => (
  <Controller
    name={item.QuestionCode}
    control={control}
    render={({ field }) => (
      <FormControl fullWidth variant="outlined">
        <Autocomplete
          {...field}
          options={item.DataValues || []}
          getOptionLabel={(option) => `${option.Val} - ${option.Name}`}
          disableClearable={true}
          renderInput={(params) => (
            <TextField
              {...params}
              label={item.QuestionContent}
              variant="outlined"
              error={!!errors?.[item.QuestionCode]}
              helperText={
                errors?.[item.QuestionCode]?.message as React.ReactNode
              }
            />
          )}
          onChange={async (_, value) => {
            field.onChange(value?.Val || "");
            const newList: Question[] = (dataQuestions?.Questions ?? []).map(
              (question: Question) => ({
                ...question,
                val:
                  question.QuestionCode === item.QuestionCode
                    ? value?.Val
                    : question.val ?? "",
              })
            );
            await dispatch(
              fetchUpdatedData({
                questions: newList ?? [],
                variables: data?.Variables ?? [],
              })
            );

          }}
          value={
            item.DataValues?.find((val) => val.Val === field.value) || undefined
          }
        />
      </FormControl>
    )}
  />
);

const MinMaxQuestion: FC<QuestionProps> = ({
  item,
  control,
  errors = {},
  dataQuestions,
  dispatch,
  data,
}) => (
  <Controller
    name={item.QuestionCode}
    control={control}
    render={({ field }) => (
      <TextField
        {...field}
        id={item.QuestionCode}
        type="number"
        variant="outlined"
        fullWidth
        placeholder="Entrez une valeur"
        inputProps={{
          min: item.MINVALUE || 0,
          max: item.MAXVALUE || 1000,
          step: 0.1,
          onWheel: (e) => {
            // Retirer le focus de l'input lors du scroll
            (e.target as HTMLElement).blur();
        }
        }}
        onChange={(e) => {
          field.onChange(parseFloat(e.target.value));
        }}
        onBlur={async () => {
          if (field.value !== undefined) {
            const newList: Question[] = (dataQuestions?.Questions ?? []).map(
              (question: Question) => ({
                ...question,
                val:
                  question.QuestionCode === item.QuestionCode
                    ? field.value.toString()
                    : question.val ?? "",
              })
            );
            await dispatch(
              fetchUpdatedData({
                questions: newList ?? [],
                variables: data?.Variables ?? [],
              })
            );
          }
        }}
        error={!!errors?.[item.QuestionCode]}
        helperText={errors?.[item.QuestionCode]?.message as React.ReactNode}
      />
    )}
  />
);

const CheckboxQuestion: FC<QuestionProps> = ({
  item,
  control,
  dataQuestions,
  dispatch,
  data,
}) => (

  
  <Controller
  name={item.QuestionCode}
  control={control}
  render={({ field }) => (
    <FormControlLabel
      control={
        <Checkbox
          {...field}
          id={item.QuestionCode}
          checked={field.value === true || field.value === "1"} 
          onChange={async (e) => {
            const newValue = e.target.checked;
            
            field.onChange(newValue); 

            const newList: Question[] = (dataQuestions?.Questions ?? []).map(
              (question: Question) => ({
                ...question,
                val:
                  question.QuestionCode === item.QuestionCode
                    ? (newValue ? "1" : "0") 
                    : question.val ?? "",
              })
            );

            await dispatch(
              fetchUpdatedData({
                questions: newList ?? [],
                variables: data?.Variables ?? [],
              })
            );
          }}
        />
      }
      label={item.QuestionContent}
    />
  )}
/>

);

const TextQuestion: FC<QuestionProps> = ({
  item,
  control,
  errors = {},
  dataQuestions,
  dispatch,
  data,
}) => (

  <Controller
    name={item.QuestionCode}
    control={control}
    render={({ field }) => (
      <TextField
        {...field}
        id={item.QuestionCode}
        type="text"
        variant="outlined"
        fullWidth
        placeholder="..."
        value={field.value || ""}
        InputProps={{
      
          // Empêcher le changement de valeur lors du scroll
          onWheel: (e) => {
              // Retirer le focus de l'input lors du scroll
              (e.target as HTMLElement).blur();
          }
      }}
        onChange={(e) => {
          field.onChange(e.target.value);
        }}
        onBlur={async () => {
          
          if (field.value !== undefined) {
            const newList: Question[] = (dataQuestions?.Questions ?? []).map(
              (question: Question) => ({
                ...question,
                val:
                  question.QuestionCode === item.QuestionCode
                    ? field.value.toString()
                    : question.val ?? "",
              })
            );            
            await dispatch(
              fetchUpdatedData({
                questions: newList ?? [],
                variables: data?.Variables ?? [],
              })
            );
          }
        }}
        error={!!errors?.[item.QuestionCode]}
        helperText={errors?.[item.QuestionCode]?.message as React.ReactNode}
      />
    )}
  />
);

export default BuildSpecialForm;
